import { FluentProvider } from "@fluentui/react-components";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, type ReactNode } from "react";

import { darkModeAtom, isRTLAtom, osDarkModeAtom } from "@/store";
import { lightTheme, darkTheme } from "./themes";

interface ThemeProviderWrapperProps {
  children: ReactNode;
}

export function ThemeProviderWrapper({ children }: ThemeProviderWrapperProps) {
  const isDarkMode = useAtomValue(darkModeAtom);
  const isRTL = useAtomValue(isRTLAtom);
  const setOsDarkMode = useSetAtom(osDarkModeAtom);
  const dir = isRTL ? "rtl" : "ltr";

  // Listen for OS dark mode changes so 'system' preference stays reactive
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => setOsDarkMode(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [setOsDarkMode]);

  useEffect(() => {
    document.documentElement.dir = dir;
  }, [dir]);

  useEffect(() => {
    document.documentElement.style.colorScheme = isDarkMode ? "dark" : "light";
  }, [isDarkMode]);

  return (
    <FluentProvider
      theme={isDarkMode ? darkTheme : lightTheme}
      dir={dir}
      style={{ height: "100%" }}
    >
      {children}
    </FluentProvider>
  );
}
